import { useMemo } from 'react'
import { Trans } from 'react-i18next'
import { Descendant } from 'slate'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  DefaultVariableResult,
  IActionFormProps,
  VARIABLE_TEXT_FEATURE_FLAG_ALL,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'
import { Button, ButtonVariant, Field, Label, SelectDropdown, Switch } from '@makyoapp/makyo-ui'

import { parseVariableText } from '@/core/app-builder/parser/variable-text'
import { convertLegacyValueToDescendant } from '@/lib/slate'
import { useForm } from '@/lib/use-form'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useDetailRowIndex } from '@/hooks/__project__/useDetailRowIndex'
import { getDataSourceKeyForComponent } from '@/shared/builder/utils/components'
import { PAPER_SIZE_MAP } from '@/shared/constants'
import { ActionInputV3 } from '@/components/ActionInputV3/ActionInputV3'
import { VariableSelectV2 } from '@/components/VariableSelectV2/VariableSelectV2'
import { VariableTextV2 } from '@/components/VariableTextV2/VariableTextV2'

type GeneratePDFFormValues = {
  filename: Descendant[] | string
  sourceColumn?: any
  targetColumn?: any
  paperSize: string
  useHtmlFormat: boolean
}

export default function ActFormGeneratePDF({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  // Context for variable text support
  const { activeLayerClient, activeLayerContainer, activeComponent, activeLayerTableId, activeLayerDetailRowId } =
    useActiveLayerContext()
  const { userEmail } = useAppAuthContext()
  const { language, userSpecific, dbClient } = useDbClientContext()

  // Get active table ID for variable text
  const dataSourceKey = getDataSourceKeyForComponent(activeComponent?.compId || '')
  const table = dataSourceKey
    ? (activeLayerContainer?.elementSettings?.[activeComponent?.id as string]?.[dataSourceKey] ?? null)
    : null
  const activeTableId = (table as any)?.table ?? table

  // Clean detail row index handling
  const { detailRowIndex } = useDetailRowIndex(0)

  // Paper size options
  const paperSizeOptions = Object.entries(PAPER_SIZE_MAP).map(([key, size]) => ({
    value: key,
    label: size.label,
  }))

  // access form store
  const { form, onSubmit: onFormSubmit } = useForm<GeneratePDFFormValues>({
    defaultValues: {
      filename: initialData?.filename,
      sourceColumn: initialData?.sourceColumn || undefined,
      targetColumn: initialData?.targetColumn || undefined,
      paperSize: initialData?.paperSize || 'A4',
      useHtmlFormat: initialData?.useHtmlFormat || false,
    },
    validate(values, setError) {
      const filenameText =
        parseVariableText(
          convertLegacyValueToDescendant(values.filename, ''),
          dbClient,
          activeLayerClient,
          userEmail,
          language,
          userSpecific,
          activeLayerTableId || activeTableId,
          activeLayerDetailRowId
        )?.trim() || ''

      if (!filenameText) {
        setError('filename', 'Please enter a filename')
      }
      if (!values.sourceColumn) {
        setError('sourceColumn', 'Please choose a Template Source')
      }
      if (!values.targetColumn) {
        setError('targetColumn', 'Please choose a Output URL')
      }
    },
  })

  // save action settings
  const handleSubmit = onFormSubmit((data) => {
    onSubmit(data)
  })

  // Validate filename text
  const filenameValue = form.getValues('filename')
  const filenameText =
    parseVariableText(
      convertLegacyValueToDescendant(filenameValue, ''),
      dbClient,
      activeLayerClient,
      userEmail,
      language,
      userSpecific,
      activeLayerTableId || activeTableId,
      activeLayerDetailRowId
    )?.trim() || ''

  const sourceColumn = form.getValues('sourceColumn')
  const targetColumn = form.getValues('targetColumn')

  const invalidForm = !filenameText || !!form.errors || !sourceColumn?.value || !targetColumn?.col

  // Feature flags for VariableTextV2 (similar to snackbar)
  const featureFlags = useMemo(() => {
    return (
      (VARIABLE_TEXT_FEATURE_FLAG_ALL &
        ~VariableTextFeatureFlag.Icon &
        ~VariableTextFeatureFlag.Image &
        ~VariableTextFeatureFlag.Column &
        ~VariableTextFeatureFlag.ColumnData) |
      VariableTextFeatureFlag.DateTime |
      VariableTextFeatureFlag.ActionOutput
    )
  }, [])

  // Column types for action inputs
  const sourceColumnTypes = useMemo(() => [ColumnTypes.Template], [])
  const targetColumnTypes = useMemo(() => [ColumnTypes.URL], [])

  return (
    <div>
      <div className='p-4 max-h-[75vh] overflow-auto min-h-[500px]'>
        {/* PDF Filename */}
        <Field className='min-h-0'>
          <Label required>PDF File Name</Label>
          <VariableTextV2
            tableId={activeTableId}
            featureFlags={
              initialData?.isActionWorkflow ? featureFlags : featureFlags & ~VariableTextFeatureFlag.ActionOutput
            }
            isTextArea={false}
            value={filenameValue as Descendant[]}
            onChange={(value) => {
              form.setValues('filename', value)
            }}
            options={initialData?.actionOptions}
            placeholder='Enter PDF filename (e.g., report-{Row ID})'
          />
          {form.errors?.filename && <div className='mk-input-error'>{form.errors.filename}</div>}
        </Field>

        {/* Source Column Selection */}
        <Field className='min-h-0'>
          <Label required>Template Source</Label>
          <ActionInputV3
            placeholder='Select source column (template type)'
            value={form.getValues('sourceColumn')}
            onChange={(data) => {
              form.setValues('sourceColumn', data)
            }}
            error={form.errors?.sourceColumn}
            columnTypes={sourceColumnTypes}
            options={[]}
          />
        </Field>

        {/* Target Column Selection */}
        <Field className='min-h-0'>
          <Label required>Output URL (URL)</Label>
          <VariableSelectV2
            disableInput
            limitType={{
              [VariableSelectFeatureFlag.CustomText]: 0,
            }}
            id='pdf-target-column'
            placeholder='Select target column (URL type)'
            value={form.getValues('targetColumn')}
            onChange={(data) => {
              form.setValues('targetColumn', data as DefaultVariableResult)
            }}
            variableColumnOptions={{
              columnTypes: targetColumnTypes,
              excludeColIds: [],
            }}
            featureFlags={
              VariableSelectFeatureFlag.SingleCell |
              VariableSelectFeatureFlag.CurrentListItem |
              VariableSelectFeatureFlag.CurrentRow
            }
            tableId={activeTableId}
            error={form.errors?.targetColumn}
          />
        </Field>

        {/* Paper Size Selection */}
        <Field className='min-h-0'>
          <Label>Paper Size</Label>
          <SelectDropdown
            value={paperSizeOptions.find((option) => option.value === form.getValues('paperSize'))}
            onChange={(option) => {
              form.setValues('paperSize', option.value)
            }}
            options={paperSizeOptions}
            optionLabel={(option) => option.label}
            renderOption={(option) => option.label}
            placeholder='Select paper size'
          />
        </Field>

        {/* HTML Format Switch */}
        <Field className='min-h-0'>
          <div className='flex items-center justify-between'>
            <Label>Use HTML Format</Label>
            <Switch
              checked={form.getValues('useHtmlFormat')}
              onClick={(checked) => {
                form.setValues('useHtmlFormat', checked)
              }}
            />
          </div>
        </Field>
        {/* PDF Styling Constraints Information */}
        <div className='px-4 py-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30'>
          <div className='flex items-start gap-3'>
            <div className='flex-shrink-0 mt-0.5'>
              <svg className='w-5 h-5 text-blue-600 dark:text-blue-400' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='flex-1'>
              <h4 className='text-sm font-medium text-blue-900 dark:text-blue-100 mb-1'>PDF Styling Guidelines</h4>
              <div className='text-sm text-blue-700 dark:text-blue-300 space-y-1'>
                <p>Your PDF templates must follow these styling constraints for optimal rendering:</p>
                <ul className='list-disc list-inside space-y-0.5 ml-2'>
                  <li>Use only inline CSS styles (no external stylesheets)</li>
                  <li>Avoid complex CSS Grid and Flexbox layouts</li>
                  <li>Don&apos;t use font-family declarations</li>
                  <li>Avoid pseudo-classes like nth-child, nth-of-type, etc.</li>
                  <li>Avoid JavaScript and interactive elements</li>
                </ul>
                <p className='text-xs mt-2 opacity-80'>
                  These constraints ensure compatibility with our PDF generation engine.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
